import {
  SignInDto,
  SignUpDto,
  MyInforDto,
  MyInforChangeDto,
  PasswordChangeDto,
  TokenDto,
  phoneNumCertificationDto,
  idSearchResultDto,
  UserListSearchDto,
  UserListDto,
  UserListResDto,
  HistorySearchDto,
  HistoryResDto,
} from '../model/user.dto';
import {
  RoomMakeDto,
  RoomsDto,
  MyRoomsDto,
  RoomPasswordDto,
  RoomInforDto,
  RoomPeoplesDto,
  EntryListsDto,
  WorkNumsDto,
  FakeWorkNumsDto,
  WorkNumListsDto,
  ModiyiListsDto,
  MonthUnitPriceDto,
  PourListSearchDto,
  PourListResDto,
  EntrySiteDto,
  WorkStartCurrentLocationListsDto,
} from '../model/room.dto';
import { publicApi, api } from './axios';

// 유저 API
export const authAPI = {
  // 로그인
  signIn: (dto: SignInDto) => {
    return publicApi.post<TokenDto>('/user/auth/signin', dto);
  },

  // 아이디 중복 체크
  idCheck: (id: string) => {
    return publicApi.get<boolean>(`/user/auth/check-passid/${id}`);
  },

  // 휴대폰 번호 인증 코드 발송
  postPhoneNumCertification: (phone: string) => {
    return publicApi.post<phoneNumCertificationDto>(`/user/cert/phone/step1`, {
      phone,
    });
  },

  // 휴대폰 번호 인증 코드 확인
  postCertificationNumConfirm: (uuid: string, code: string) => {
    return publicApi.post<idSearchResultDto>(`/user/cert/phone/step2`, {
      uuid,
      code,
    });
  },

  // 닉네임 중복 체크
  nicknameCheck: (nickName: string) => {
    return publicApi.get<boolean>(`/user/auth/check-nickname/${nickName}`);
  },

  // 회원가입
  signUp: (dto: SignUpDto) => {
    return publicApi.post<void>(`/user/auth/signup`, dto);
  },

  // 아이디 찾기 휴대폰 번호 인증 코드 발송
  postIdSerachPhoneNumCertification: (phone: string) => {
    return publicApi.post<phoneNumCertificationDto>(`/user/cert/passid/step1`, {
      phone,
    });
  },

  // 아이디 찾기 휴대폰 번호 인증 코드 확인
  postIdSerachCertificationNumConfirm: (uuid: string, code: string) => {
    return publicApi.post<idSearchResultDto>(`/user/cert/passid/step2`, {
      uuid,
      code,
    });
  },

  // 비밀번호 찾기 휴대폰 번호 인증 코드 발송
  postPasswordSerachPhoneNumCertification: (passid: string, phone: string) => {
    return publicApi.post<phoneNumCertificationDto>(
      `/user/cert/password/step1`,
      {
        passid,
        phone,
      },
    );
  },

  // 비밀번호 찾기 휴대폰 번호 인증 코드 확인
  postPasswordSerachCertificationNumConfirm: (uuid: string, code: string) => {
    return publicApi.post<void>(`/user/cert/password/step2`, {
      uuid,
      code,
    });
  },

  // 비밀번호 찾기 비밀번호 재설정
  putPasswordChange: (uuid: string, password: string) => {
    return publicApi.put<void>(`/user/cert/password/step3`, {
      uuid,
      password,
    });
  },

  // 토큰 갱신
  getNewToken: (refreshToken: string) => {
    return publicApi.get<TokenDto>('/user/auth/reissue', {
      headers: {
        Authorization: `Bearer ${refreshToken}`,
      },
    });
  },

  // 유저 정보 조회
  getMyInfor: () => {
    return api.get<MyInforDto>('/user/info/find/profile');
  },

  // 비밀번호 변경
  passwordChange: (dto: PasswordChangeDto) => {
    return api.put<void>('/user/info/update/password', dto);
  },

  // 회원 정보 변경
  putMyInfor: (dto: MyInforChangeDto) => {
    return api.put<void>('/user/info/update/profile', dto);
  },

  // 개인정보 처리 방침 동의 변경
  putPrivacyPolicyAgreed: (privacyPolicyAgreed: boolean) => {
    return api.put<void>('/user/info/update/privacy-policy-agreed', {
      privacyPolicyAgreed,
    });
  },
};

// 방 API
export const roomAPI = {
  // 방 제목 중복 체크
  getRoomTitleDuplication: (roomTitle: string) => {
    return api.get<void>(`/room/check/${roomTitle}`);
  },

  // 방 만들기
  roomMake: (dto: RoomMakeDto) => {
    return api.post<void>('/room', dto);
  },

  // 방 목록
  getRooms: () => {
    return api.get<RoomsDto>('/room');
  },

  // 내 방 목록
  getMyRooms: () => {
    return api.get<MyRoomsDto>('/room/mine');
  },

  // 방 입장
  roomEntry: (id: number, dto: RoomPasswordDto) => {
    return api.post<void>(`/participation/${id}`, dto);
  },

  // 방 정보 조회
  getRoomInfor: (roomId: number) => {
    return api.get<RoomInforDto>(`/room/${roomId}`);
  },

  // 방 업무 시작 인원 위치 조회
  getWorkStartCurrentLocation: (roomId: number, keyword?: string) => {
    return api.get<WorkStartCurrentLocationListsDto>(`/room/current-location`, {
      params: { roomId, keyword },
    });
  },

  // 현재 위치 업데이트
  postCurrentLocation: (
    roomId: number,
    latitude: number,
    longitude: number,
  ) => {
    return api.post<void>(`/participation/location/${roomId}`, {
      latitude,
      longitude,
    });
  },

  // 업무 시작 여부 조회
  getWorkStart: (roomId: number) => {
    return api.get<{ isWorking: boolean }>(`/participation/working/${roomId}`);
  },

  // 업무 시작 여부 변경
  postWorkStart: (roomId: number) => {
    return api.post<void>(`/participation/working/${roomId}`);
  },

  // 입차지 생성
  postEntrySite: (roomId: number, list: EntrySiteDto) => {
    return api.post<void>(`/entry-site/${roomId}`, list);
  },

  // 입차지 수정
  putEntrySite: (roomId: number, list: EntrySiteDto) => {
    return api.put<void>(`/entry-site/${roomId}`, list);
  },

  // 입차지 조회
  getEntrySite: (roomId: number) => {
    return api.get<EntrySiteDto>(`/entry-site/${roomId}`);
  },

  // 입차지 삭제
  delEntrySite: (roomId: number) => {
    return api.delete<void>(`/entry-site/${roomId}`);
  },

  // 입출차 목록 조회
  getEntryLists: (lineId: number) => {
    return api.get<EntryListsDto>(`/entry/${lineId}`);
  },

  // 방 참가자 인원 조회
  getRoomPeoples: (roomId: number) => {
    return api.get<RoomPeoplesDto>(`/participation/${roomId}`);
  },

  // 방 공지사항 수정(방장 기능)
  putNotice: (roomId: number, notice: string) => {
    return api.put<void>(`/room/notice/${roomId}`, { notice });
  },

  // 방 라인 추가(방장 기능)
  postLineAdd: (roomId: number) => {
    return api.post<void>(`/room/line/${roomId}`);
  },

  // 방 라인 이동(방장 기능)
  putLineMove: (roomId: string, lineId: number, entryIds: number[]) => {
    return api.put<void>(`/room/line/${roomId}`, { lineId, entryIds });
  },

  // 방 라인 삭제(방장 기능)
  delLine: (roomId: number) => {
    return api.delete<void>(`/room/line/?ids=${roomId}`);
  },

  // 출차 승인(방장 기능)
  delOrderExit: (lineId: number, ids: string[]) => {
    return api.delete<void>(`/entry/order/${lineId}?ids=${ids}`);
  },

  // 입차 취소(방장 기능)
  delOrderCancle: (lineId: number, ids: string[]) => {
    return api.delete<void>(`/entry/cancel/order/${lineId}?ids=${ids}`);
  },

  // 입차 가능한 인원 조회(방장 기능)
  getEntryPossiblePeoples: (roomId: number) => {
    return api.get<RoomPeoplesDto>(`/participation/available/${roomId}`);
  },

  // 입차 시키기(방장 기능)
  postEntryOrder: (lineId: number, userId: number) => {
    return api.post<void>(`/entry/order/${lineId}`, { userId });
  },

  // 입차 순서 변경(방장 기능)
  putOrderList: (lineId: number, list: ModiyiListsDto) => {
    return api.put<void>(`/entry/list/${lineId}`, { list });
  },

  // 입차 자동 정렬 기능(방장 기능)
  putOrderAutoSort: (lineId: number, autoSort: boolean, fixedTurn: number) => {
    return api.put<void>(`/room/line/auto-sort/${lineId}`, {
      autoSort,
      fixedTurn,
    });
  },

  // 입차 여부 확인
  getEntryStatus: (roomId: number) => {
    return api.get<{ lineIds: string[] }>(
      `/participation/entry-status/${roomId}`,
    );
  },

  // 입차하기
  postEntrance: (lineId: number) => {
    return api.post<void>(`/entry/${lineId}`);
  },

  // 입차 취소
  delEntranceCancle: (lineId: number) => {
    return api.delete<void>(`/entry/cancel/${lineId}`);
  },

  // 출차하기
  delExit: (lineId: number) => {
    return api.delete<void>(`/entry/${lineId}`);
  },

  // 끼어들기
  putInterrupt: (lineId: number, currentTurn: number, targetTurn: number) => {
    return api.put<void>(`/entry/${lineId}`, { currentTurn, targetTurn });
  },

  // 용차 내보내기(방장기능)
  delRoomOutBorroweds: (ids: string[]) => {
    return api.delete<void>(`/participation?ids=${ids}`);
  },

  // 대기 추가/취소(방장기능)
  putWait: (lineId: number, id: string) => {
    return api.put<void>(`/entry/wait/${lineId}?id=${id}`);
  },

  // 방 삭제(방장 기능)
  delRoom: (ids: string[]) => {
    return api.delete<void>(`/room?ids=${ids}`);
  },

  // 방 나가기
  delRoomOut: (ids: string[]) => {
    return api.delete<void>(`/participation?ids=${ids}`);
  },

  // 연도/월 탕수 조회
  getDateWorkNums: (year: number, month: number, day?: number) => {
    return api.get<WorkNumListsDto>(
      `/cycle?year=${year}&month=${month}&day=${day}`,
    );
  },

  // 오늘탕수/이번달탕수 조회
  getWorkNums: (userId: number) => {
    return api.get<WorkNumsDto>(`/cycle/union?ids=${userId}`);
  },

  // 탕수 수정
  putWorkNum: (cycleId: number, cycle: number) => {
    return api.put<void>(`/cycle/${cycleId}`, { cycle });
  },

  // 가탕 조회
  getFakeWorkNums: () => {
    return api.get<FakeWorkNumsDto>(`/cycle/addition`);
  },

  // 가탕 수정
  putFakeWorkNum: (additionId: number, addition: number) => {
    return api.put<void>(`/cycle/addition/${additionId}`, { addition });
  },

  // 월별 단가 조회
  getMonthUnitPrice: (year: number, month: number) => {
    return api.get<MonthUnitPriceDto>(
      `/cycle/monthlyUnitRate?year=${year}&month=${month}`,
    );
  },

  // 월별 단가 수정
  putMonthUnitPrice: (monthlyUnitRateId: number, unitRate: number) => {
    return api.put<void>(`/cycle/monthlyUnitRate/${monthlyUnitRateId}`, {
      unitRate,
    });
  },

  // 타설 하기
  putPour: (lineId: number, minute: number) => {
    return api.put<void>(`/entry/pour/${lineId}`, {
      minute,
    });
  },

  // 타설 목록 조회
  getPourList: (dto: PourListSearchDto) => {
    return api.get<PourListResDto>(`/entry/pour/${dto.lineId}`, {
      params: dto,
    });
  },

  // 참여자 위치 조회 권한 일괄 설정
  putLocationShare: (
    roomId: number,
    dto: { participantIds: number[]; isLocationViewer: boolean },
  ) => {
    return api.put<void>(`/participation/${roomId}/permissions`, dto);
  },
};

// 관리자 API
export const adminAPI = {
  // 로그인
  adminSignIn: (dto: SignInDto) => {
    return publicApi.post<TokenDto>('/admin/signin', dto);
  },

  // 회원 목록 조회
  getUserList: (dto: UserListSearchDto) => {
    return api.get<UserListResDto>(`/admin/user`, { params: dto });
  },

  // 히스토리 조회
  getHistory: (dto: HistorySearchDto) => {
    return api.get<HistoryResDto>(`/admin/history`, { params: dto });
  },
};
